import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ExplodedView {
  id: string;
  user_id: string;
  title: string | null;
  description: string | null;
  brand: string | null;
  appliance_type: string | null;
  models: string[];
  workflow_item_id: string | null;
  file_path: string;
  file_type: string; // 'image' | 'pdf'
  mime_type: string | null;
  file_size: number | null;
  original_filename: string | null;
  created_at: string;
  updated_at: string;
}

const norm = (s?: string | null) => (s || "").trim().toLowerCase();

/** Normalize brand name (e.g. BSH manufacturing group maps to Bosch). */
const normalizeBrand = (brand?: string | null): string => {
  const b = (brand || "").trim();
  if (!b) return "";
  const upper = b.toUpperCase();
  if (upper === "BSH") return "bosch";
  return b.toLowerCase();
};

/** Normalize appliance type / category slug to comparable token. */
const normalizeType = (t?: string | null): string => {
  const v = (t || "").trim().toLowerCase();
  if (!v) return "";
  // Treat "washing-machine", "washing_machine", "washing machine" as same.
  return v.replace(/[\s_-]+/g, "");
};

/** Check if two appliance type tokens refer to the same category. */
const typesMatch = (a: string, b: string): boolean => {
  if (!a || !b) return false;
  if (a === b) return true;
  return a.includes(b) || b.includes(a);
};

/** Fetch all exploded views (admin lists). */
export function useAllExplodedViews() {
  return useQuery({
    queryKey: ["exploded_views", "all"],
    queryFn: async (): Promise<ExplodedView[]> => {
      const { data, error } = await supabase
        .from("exploded_views")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ExplodedView[];
    },
  });
}

/** Fetch exploded views matching an appliance brand + type (and optional model). */
export function useMatchingExplodedViews(
  brand?: string | null,
  applianceType?: string | null,
  modelNumber?: string | null,
  options?: { requireModelMatch?: boolean },
) {
  const requireModelMatch = options?.requireModelMatch ?? false;
  return useQuery({
    queryKey: [
      "exploded_views",
      "match",
      normalizeBrand(brand),
      normalizeType(applianceType),
      norm(modelNumber),
      requireModelMatch ? "strict" : "loose",
    ],
    enabled: Boolean(brand || applianceType),
    queryFn: async (): Promise<ExplodedView[]> => {
      const targetBrand = normalizeBrand(brand);
      const targetType = normalizeType(applianceType);
      const targetModel = norm(modelNumber);

      // Strict mode: without a model number we can't match anything meaningfully.
      if (requireModelMatch && (!targetBrand || !targetType || !targetModel)) {
        return [];
      }

      const { data, error } = await supabase.from("exploded_views").select("*");
      if (error) throw error;
      const all = (data ?? []) as ExplodedView[];

      const filtered = all.filter((v) => {
        const vb = normalizeBrand(v.brand);
        const vt = normalizeType(v.appliance_type);

        if (requireModelMatch) {
          // Strict: brand, type, and model must all be present and match.
          if (!vb || vb !== targetBrand) return false;
          if (!vt || !typesMatch(vt, targetType)) return false;
          if (!v.models || v.models.length === 0) return false;
          return v.models.some((mm) => {
            const nm = norm(mm);
            return nm && (nm === targetModel || targetModel.includes(nm) || nm.includes(targetModel));
          });
        }

        // Brand: must match if both sides have a brand. Allow view with no brand (generic) only if type matches.
        const brandOk = targetBrand && vb ? vb === targetBrand : !vb;
        // Type: must match if both sides have a type. Allow view with no type only if brand matches.
        const typeOk = targetType && vt ? typesMatch(vt, targetType) : !vt;

        // Require BOTH dimensions to match (with empty = wildcard on the view's side only).
        if (!brandOk || !typeOk) return false;

        // If the view targets specific models, require model match too (when we know the model).
        if (v.models && v.models.length > 0 && targetModel) {
          return v.models.some((mm) => {
            const nm = norm(mm);
            return nm && (nm === targetModel || targetModel.includes(nm) || nm.includes(targetModel));
          });
        }
        return true;
      });

      // Score for ordering: exact model > exact brand+type > generic.
      const score = (v: ExplodedView) => {
        let s = 0;
        if (normalizeBrand(v.brand) === targetBrand && targetBrand) s += 2;
        if (normalizeType(v.appliance_type) === targetType && targetType) s += 2;
        if (targetModel && v.models?.some((mm) => norm(mm) === targetModel)) s += 3;
        return s;
      };

      return filtered.sort((a, b) => score(b) - score(a));
    },
  });
}

/** Resolve a signed URL for a given file path in repair-guides bucket. */
export async function getExplodedViewSignedUrl(filePath: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from("repair-guides")
    .createSignedUrl(filePath, 600);
  if (error) return null;
  return data?.signedUrl ?? null;
}
